import { useEffect, useState } from "react";
import api from "@/lib/api";

const get = (u) => api.get(u, { __flSkipReadCache: true }).then((r) => r.data).catch(() => null);
const ISSUED = ["sent", "viewed", "partially_paid", "paid", "overdue"];

// Loads every overview figure from existing job endpoints (all company-scoped server side).
export function useJobOverviewData(job, { commercial, office }) {
  const [d, setD] = useState({ loaded: false, tasks: [], diary: [], photos: [], variations: [], activity: null, pendingMaterials: 0 });
  useEffect(() => {
    let ok = true;
    const id = job.id;
    Promise.all([
      commercial ? get(`/jobs/${id}/budget`) : null,
      commercial ? get(`/jobs/${id}/cost_breakdown`) : null,
      commercial ? get(`/customer-invoices?job_id=${encodeURIComponent(id)}`) : null,
      commercial ? get(`/jobs/${id}/charge-up/summary`) : null,
      get(`/jobs/${id}/hours_summary`),
      get(`/jobs/${id}/tasks`),
      get(`/jobs/${id}/diary`),
      get(`/jobs/${id}/photos`),
      get(`/jobs/${id}/activity`),
      get(`/jobs/${id}/variations`),
      office ? get(`/jobs/${id}/site-materials`) : null,
    ]).then(([budget, costs, invoices, billing, hours, tasks, diary, photos, activity, variations, siteMaterials]) => {
      if (!ok) return;
      const issued = Array.isArray(invoices) ? invoices.filter((i) => ISSUED.includes(i.status) && i.invoice_type !== "credit_note") : null;
      setD({
        loaded: true, budget, costs, billing, hours, activity,
        invoiced: issued && invoices.length < 500 ? issued.reduce((s, i) => s + Number(i.total || 0), 0) : null,
        tasks: tasks?.items || [], tasksOk: tasks != null,
        diary: diary?.items || [], diaryOk: diary != null,
        photos: photos?.items || [], photoTotal: photos?.total ?? (photos?.items || []).length,
        variations: Array.isArray(variations) ? variations : variations?.items || [],
        pendingMaterials: (siteMaterials?.materials || []).filter((m) => m.status === "pending").length,
      });
    });
    return () => { ok = false; };
  }, [job.id, commercial, office]);
  return d;
}

export function costFigures(costs) {
  if (!costs) return null;
  const plantHours = (costs.fleet?.total_hours || 0) + (costs.gear?.total_hours || 0);
  const plantCost = (costs.fleet?.total_cost || 0) + (costs.gear?.total_cost || 0);
  const matCost = (costs.materials?.total_cost || 0) + (costs.invoices?.total_cost || 0);
  const matCount = (costs.materials?.rows || []).length + (costs.invoices?.material_summary || []).length;
  return {
    labourHours: costs.labour?.total_hours || 0, labourCost: costs.labour?.total_cost || 0,
    plantHours, plantCost, matCost, matCount, reviewCount: costs.materials?.review_count || 0,
    total: costs.totals?.total_cost ?? null,
  };
}

export const nzd = (v, dp = 0) => v == null ? "—" : new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);
export const hrs = (v) => `${Number(v || 0).toLocaleString("en-NZ", { maximumFractionDigits: 1 })}h`;
export const tone = (pct) => pct == null ? "" : pct > 100 ? "over" : pct >= 80 ? "warn" : "";
